import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { vendorsApi, projectsApi, evaluationsApi, api, type VendorResponse as ApiVendorResponse, type Evaluation } from '../lib/api'
import { Button } from '../components/ui/Button'
import { StatusBadge, Badge } from '../components/ui/Badge'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { Select } from '../components/ui/Input'
import { Tooltip } from '../components/ui/Tooltip'
import FileViewer from '../components/FileViewer'
import { useChat } from '../hooks/useChat'
import { useUpdateEvaluation } from '../lib/hooks'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  ArrowLeft,
  ChevronDown,
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileText,
  Upload,
  Edit
} from 'lucide-react'

interface FileItem {
  file_path: string
  file_name: string
  file_size: number
  file_type: string
}

// Markdown renderer component for AI assistant messages
const MarkdownRenderer = ({ content }: { content: string }) => (
  <div className="prose prose-sm max-w-none">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Headings
        h1: ({node, ...props}) => (
          <h1 className="text-2xl font-bold mt-6 mb-3 text-[#303030]" {...props} />
        ),
        h2: ({node, ...props}) => (
          <h2 className="text-xl font-bold mt-5 mb-2 text-[#303030]" {...props} />
        ),
        h3: ({node, ...props}) => (
          <h3 className="text-lg font-semibold mt-4 mb-2 text-[#303030]" {...props} />
        ),

        // Lists
        ul: ({node, ...props}) => (
          <ul className="list-disc pl-6 my-2 space-y-1" {...props} />
        ),
        ol: ({node, ...props}) => (
          <ol className="list-decimal pl-6 my-2 space-y-1" {...props} />
        ),
        li: ({node, ...props}) => (
          <li className="text-[#303030] leading-relaxed" {...props} />
        ),

        // Text formatting
        strong: ({node, ...props}) => (
          <strong className="font-semibold text-[#303030]" {...props} />
        ),
        em: ({node, ...props}) => (
          <em className="italic text-[#303030]" {...props} />
        ),
        p: ({node, ...props}) => (
          <p className="my-2 text-[#303030] leading-relaxed" {...props} />
        ),

        // Code
        code: ({node, inline, ...props}) =>
          inline
            ? <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" {...props} />
            : <code className="block bg-gray-100 p-3 rounded text-sm font-mono my-2 overflow-x-auto" {...props} />,

        // Links
        a: ({node, ...props}) => (
          <a className="text-[#7035c4] hover:underline" {...props} />
        ),

        // Blockquotes
        blockquote: ({node, ...props}) => (
          <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-2" {...props} />
        ),

        // Horizontal rules
        hr: ({node, ...props}) => (
          <hr className="my-4 border-gray-300" {...props} />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
)

export const VendorResponseDetailPage: React.FC = () => {
  const { projectId, responseId } = useParams<{ projectId: string; responseId: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'chat' | 'evaluations'>('evaluations')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedFileIndex, setSelectedFileIndex] = useState(0)
  const [showFileDropdown, setShowFileDropdown] = useState(false)
  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null)
  const [isLoadingFile, setIsLoadingFile] = useState(false)
  const [pdfHighlights, setPdfHighlights] = useState<Array<{
    id: string
    text: string
    startIndex: number
    length: number
    page: number
  }>>([])
  const [editingEvaluationId, setEditingEvaluationId] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowFileDropdown(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Function to highlight text in PDF
  const highlightEvidenceInPDF = (evaluation: Evaluation) => {
    console.log('highlightEvidenceInPDF called:', {
      hasEvidence: !!evaluation.evidence,
      sourcePage: evaluation.source_page,
      sourceFilePath: evaluation.source_file_path
    })

    // Allow navigation even without source_page if we have source_file_path
    if (evaluation.evidence || evaluation.source_file_path) {
      const highlight = {
        id: evaluation.id,
        text: evaluation.evidence || '',
        startIndex: evaluation.text_start_index || 0,
        length: evaluation.text_length || (evaluation.evidence?.length || 0),
        page: evaluation.source_page || 1
      }

      // Clear existing highlights first to ensure fresh state
      setPdfHighlights([])

      // Small delay to ensure the component processes the cleared state
      setTimeout(() => {
        processHighlight()
      }, 50)

      const processHighlight = () => {
        // Switch to the correct file if evaluation has source_file_path
        if (evaluation.source_file_path && vendorResponse?.files) {
          console.log('Looking for file:', evaluation.source_file_path)
          console.log('Available files:', vendorResponse.files.map(f => ({ name: f.file_name, path: f.file_path })))

          const fileIndex = vendorResponse.files.findIndex(file => {
            // Try exact filename match first
            if (file.file_name === evaluation.source_file_path) {
              console.log('Found exact filename match:', file.file_name)
              return true
            }

            // Try exact file path match
            if (file.file_path === evaluation.source_file_path) {
              console.log('Found exact path match:', file.file_path)
              return true
            }

            // Try partial path match (source_file_path contained in file_path)
            if (file.file_path.includes(evaluation.source_file_path)) {
              console.log('Found partial path match:', file.file_path, 'contains', evaluation.source_file_path)
              return true
            }

            // Try case-insensitive filename match
            if (file.file_name.toLowerCase() === evaluation.source_file_path.toLowerCase()) {
              console.log('Found case-insensitive filename match:', file.file_name)
              return true
            }

            return false
          })

          console.log('File search result - index:', fileIndex, 'current index:', selectedFileIndex)

          if (fileIndex !== -1 && fileIndex !== selectedFileIndex) {
            console.log('Switching to file index:', fileIndex, 'for file:', evaluation.source_file_path)

            // First switch the file
            setSelectedFileIndex(fileIndex)

            // Then wait for the PDF viewer to load the new file before setting page
            setTimeout(() => {
              setPdfHighlights([highlight])
              setCurrentPage(evaluation.source_page || 1)
              console.log('Set page and highlights after file switch:', evaluation.source_page || 1)
            }, 1500) // Give more time for the new PDF to load
          } else if (fileIndex !== -1) {
            // Same file, can set immediately
            console.log('Setting page immediately (same file):', evaluation.source_page || 1)
            setPdfHighlights([highlight])
            setCurrentPage(evaluation.source_page || 1)
          } else {
            console.warn('Could not find matching file for:', evaluation.source_file_path)
            // Still jump to the page on current file as fallback
            setPdfHighlights([highlight])
            setCurrentPage(evaluation.source_page || 1)
          }
        } else {
          // No source file path - this should not happen with proper evaluations
          console.error('MISSING source_file_path in evaluation:', evaluation)
          console.log('Setting page (no source file path):', evaluation.source_page || 1)
          setPdfHighlights([highlight])
          setCurrentPage(evaluation.source_page || 1)
        }
        
        // Switch to the evaluations tab so users can see both the evaluation and PDF
        setActiveTab('evaluations')
        
        // Log for debugging
        console.log('Highlighting evidence:', {
          evaluationId: evaluation.id,
          evidence: evaluation.evidence,
          evidenceLength: evaluation.evidence?.length,
          page: evaluation.source_page,
          startIndex: evaluation.text_start_index,
          length: evaluation.text_length,
          sourceFilePath: evaluation.source_file_path,
          requirement: evaluation.requirement
        })
      }
    } else {
      console.warn('Cannot navigate - no evidence or source_file_path:', evaluation)
    }
  }
  
  // Initialize AI Chat
  const chat = useChat({
    projectId: projectId!,
    vendorResponseId: responseId!,
    userId: 'user_1', // TODO: Get from authentication context
    useWebSocket: false // Temporarily disabled WebSocket, using HTTP fallback
  })

  // Initialize evaluation update hook
  const updateEvaluation = useUpdateEvaluation()

  // AI Assistant action handlers
  const handleSummarizeQualifications = async () => {
    await chat.summarizeQualifications()
  }

  const handleListMissingRequirements = async () => {
    await chat.listMissingRequirements()
  }

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Handle evaluation result change
  const handleEvaluationChange = async (evaluationId: string, newResult: 'meets' | 'partially_meets' | 'not_met') => {
    try {
      await updateEvaluation.mutateAsync({
        evaluationId,
        data: { result: newResult }
      })
      setEditingEvaluationId(null)
    } catch (error) {
      console.error('Failed to update evaluation:', error)
    }
  }

  // Toggle editing mode for evaluation
  const toggleEditEvaluation = (evaluationId: string) => {
    setEditingEvaluationId(editingEvaluationId === evaluationId ? null : evaluationId)
  }

  // Fetch vendor response details
  const { data: vendorResponse, isLoading } = useQuery(
    ['vendorResponse', responseId],
    () => vendorsApi.get(responseId!),
    {
      enabled: !!responseId,
    }
  )

  // Fetch project details
  const { data: project } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId!),
    {
      enabled: !!projectId,
    }
  )

  // Fetch evaluations for this response
  const { data: evaluations, isLoading: isLoadingEvaluations } = useQuery(
    ['evaluations', responseId],
    () => evaluationsApi.getResponseEvaluations(responseId!),
    {
      enabled: !!responseId,
    }
  )


  const getEvaluationIcon = (result: string) => {
    switch (result) {
      case 'meets':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'partially_meets':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />
      case 'not_met':
        return <XCircle className="w-4 h-4 text-red-600" />
      default:
        return null
    }
  }

  const getEvaluationBadge = (result: string) => {
    switch (result) {
      case 'meets':
        return <Badge variant="success">Yes</Badge>
      case 'partially_meets':
        return <Badge variant="warning">Partially</Badge>
      case 'not_met':
        return <Badge variant="destructive">No</Badge>
      default:
        return null
    }
  }

  // Get overall status from backend (uses fit score calculation)
  const getOverallStatusDisplay = () => {
    const backendStatus = vendorResponse?.overall_requirement_status || 'pending'

    switch (backendStatus) {
      case 'meets':
        return {
          status: 'meets',
          label: 'Meets Requirements',
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          iconColor: 'text-green-600'
        }
      case 'partially_meets':
        return {
          status: 'partially_meets',
          label: 'Partially Meets Requirements',
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          iconColor: 'text-yellow-600'
        }
      case 'not_met':
        return {
          status: 'not_met',
          label: 'Does Not Meet Requirements',
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          iconColor: 'text-red-600'
        }
      default:
        return {
          status: 'pending',
          label: 'Pending Evaluation',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          iconColor: 'text-gray-500'
        }
    }
  }

  const overallStatus = getOverallStatusDisplay()

  // Get MIME type based on file extension
  const getMimeType = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase()
    const mimeTypes: Record<string, string> = {
      'pdf': 'application/pdf',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'xls': 'application/vnd.ms-excel',
      'xlsm': 'application/vnd.ms-excel.sheet.macroEnabled.12',
      'csv': 'text/csv',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'doc': 'application/msword'
    }
    return mimeTypes[ext || ''] || 'application/octet-stream'
  }

  // Fetch file as blob and create object URL
  const loadFileAsBlob = async (filePath: string, fileName: string) => {
    setIsLoadingFile(true)
    try {
      // Remove 'uploads/' prefix if it exists since the streaming endpoint expects relative path
      const cleanPath = filePath.startsWith('uploads/') ? filePath.substring(8) : filePath

      // Use the configured API instance which includes auth interceptors
      const response = await api.get(`/files/stream/${cleanPath}`, {
        responseType: 'blob'
      })

      // Get MIME type based on file name
      const mimeType = getMimeType(fileName)

      const blob = new Blob([response.data], { type: mimeType })
      const url = URL.createObjectURL(blob)
      setFileBlobUrl(url)
    } catch (error) {
      console.error('Error loading file:', error)
      setFileBlobUrl(null)
    } finally {
      setIsLoadingFile(false)
    }
  }


  // Load file when selected file changes
  useEffect(() => {
    if (vendorResponse?.files?.[selectedFileIndex]) {
      const file = vendorResponse.files[selectedFileIndex]
      loadFileAsBlob(file.file_path, file.file_name)
      // Reset controls when switching files
      setCurrentPage(1)
      setPdfHighlights([])
    }
    
    // Cleanup previous blob URL
    return () => {
      if (fileBlobUrl) {
        URL.revokeObjectURL(fileBlobUrl)
      }
    }
  }, [selectedFileIndex, vendorResponse])

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (fileBlobUrl) {
        URL.revokeObjectURL(fileBlobUrl)
      }
    }
  }, [])


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#fdfefe] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-[#ecf0f1] h-[104px] flex items-center px-6">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate(`/projects/${projectId}?tab=responses`)}
          className="mr-4"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold">{project?.title || 'Loading...'}</h1>
          <span className="text-lg font-semibold">|</span>
          <h2 className="text-lg font-semibold">{vendorResponse?.vendor_name || 'Loading...'}</h2>
          <span className="text-lg font-semibold">|</span>
          <div className="relative" ref={dropdownRef}>
            <button
              className="flex items-center gap-1 text-lg font-semibold hover:bg-gray-100 px-2 py-1 rounded"
              onClick={() => setShowFileDropdown(!showFileDropdown)}
            >
              <span>
                {vendorResponse?.files?.[selectedFileIndex]?.file_name || 'Loading...'}
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>
            
            {showFileDropdown && vendorResponse?.files && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 min-w-[300px]">
                {vendorResponse.files.map((file: FileItem, index: number) => (
                  <button
                    key={index}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                      selectedFileIndex === index ? 'bg-blue-50 text-blue-700' : ''
                    }`}
                    onClick={() => {
                      setSelectedFileIndex(index)
                      setShowFileDropdown(false)
                    }}
                  >
                    <div className="font-medium text-sm">{file.file_name}</div>
                    <div className="text-xs text-gray-500">
                      {Math.round(file.file_size / 1024 / 1024 * 100) / 100} MB
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={`ml-auto ${overallStatus.bgColor} px-2 py-1 rounded flex items-center gap-2`}>
          {overallStatus.status === 'meets' && <CheckCircle className={`w-4 h-4 ${overallStatus.iconColor}`} />}
          {overallStatus.status === 'partially_meets' && <AlertTriangle className={`w-4 h-4 ${overallStatus.iconColor}`} />}
          {overallStatus.status === 'not_met' && <XCircle className={`w-4 h-4 ${overallStatus.iconColor}`} />}
          {overallStatus.status === 'pending' && <AlertTriangle className={`w-4 h-4 ${overallStatus.iconColor}`} />}
          <span className={`text-xs font-bold ${overallStatus.textColor}`}>{overallStatus.label}</span>
        </div>
      </div>

      <div className="flex flex-1">
        {/* PDF Viewer Section */}
        <div className="w-[65%] bg-[#ecf0f1] p-4 relative">

          {/* PDF Viewer */}
          <div className="mt-4 mx-auto w-full h-[calc(100vh-120px)] px-4 relative">
            {isLoadingFile ? (
              <div className="bg-white rounded border border-[#b2bbbc] shadow-lg h-full flex items-center justify-center">
                <div className="text-center p-6">
                  <LoadingSpinner size="lg" />
                  <p className="text-center text-gray-600 mt-4">Loading file...</p>
                </div>
              </div>
            ) : fileBlobUrl ? (
              <FileViewer
                fileUrl={fileBlobUrl}
                fileName={vendorResponse?.files?.[selectedFileIndex]?.file_name}
                fileType={vendorResponse?.files?.[selectedFileIndex]?.file_type}
                highlights={pdfHighlights}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onHighlightClick={(highlight) => {
                  console.log('Highlight clicked:', highlight)
                }}
              />
            ) : (
              <div className="bg-white rounded border border-[#b2bbbc] shadow-lg h-full flex items-center justify-center">
                <div className="text-center p-6">
                  <div className="w-24 h-24 mx-auto mb-6">
                    <div className="w-full h-full bg-blue-100 rounded-full flex items-center justify-center">
                      <FileText className="w-12 h-12 text-blue-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-center mb-3">No File Available</h3>
                  <p className="text-center text-gray-600">
                    Unable to load the selected file
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-[35%] bg-white border-l border-[#ecf0f1] flex flex-col">
          {/* Tabs */}
          <div className="flex border-b border-[#ecf0f1]">
            {/* <button
              className={`flex-1 px-4 py-4 text-sm font-semibold border-b ${
                activeTab === 'chat'
                  ? 'border-black text-black'
                  : 'border-transparent text-[#7e8c8d] hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('chat')}
            >
              AI Assistant
              {chat.isConnected && (
                <span className="ml-2 w-2 h-2 bg-green-500 rounded-full"></span>
              )}
            </button> */}
            <button
              className={`flex-1 px-4 py-4 text-sm font-semibold border-b ${
                activeTab === 'evaluations'
                  ? 'border-black text-black'
                  : 'border-transparent text-[#7e8c8d] hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('evaluations')}
            >
              Evaluations
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1 flex flex-col">
            {/* AI Chat Tab */}
            {activeTab === 'chat' && (
              <>
                {/* Chat Messages */}
                <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                  {chat.messages && chat.messages.filter(Boolean).map((message) => (
                    <div key={message?.message_id || Math.random()} className="space-y-2">
                      <div className="flex items-start gap-3">
                        {message?.role === 'assistant' ? (
                          <div className="w-[45px] h-[45px] bg-[#f9f5ff] rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-[#7035c4] font-bold text-sm">P</span>
                          </div>
                        ) : (
                          <div className="w-[45px] h-[45px] bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-gray-600 font-bold text-sm">U</span>
                          </div>
                        )}
                        <div className="flex-1">
                          <MarkdownRenderer content={message?.content || ''} />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Typing indicator */}
                  {(chat.isTyping || chat.isLoading) && (
                    <div className="space-y-2">
                      <div className="flex items-start gap-3">
                        <div className="w-[45px] h-[45px] bg-[#f9f5ff] rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-[#7035c4] font-bold text-sm">P</span>
                        </div>
                        <div className="flex-1">
                          {chat.currentResponse ? (
                            <MarkdownRenderer content={chat.currentResponse} />
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                              </div>
                              <span className="text-sm text-gray-500">AI is thinking...</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Error message */}
                  {chat.error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-red-800 text-sm">{chat.error}</p>
                      <button 
                        onClick={chat.clearError}
                        className="text-red-600 hover:text-red-800 text-sm mt-1"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                </div>

                {/* AI Assistant */}
                <div className="px-4 py-3 border-t border-[#ecf0f1]">
                  <p className="text-xs font-medium text-gray-900 mb-3">AI Assistant</p>
                  <div className="space-y-2">
                    <button
                      onClick={chat.draftFollowUpEmail}
                      disabled={chat.isLoading}
                      className="w-full p-2 text-left hover:bg-gray-50 text-sm text-[#657071] rounded border border-gray-200 disabled:opacity-50"
                    >
                      ✉️ Draft follow-up email for missing items
                    </button>
                    <button
                      onClick={handleSummarizeQualifications}
                      disabled={chat.isLoading}
                      className="w-full p-2 text-left hover:bg-gray-50 text-sm text-[#657071] rounded border border-gray-200 disabled:opacity-50"
                    >
                      📋 Summarize key qualifications
                    </button>
                    <button
                      onClick={handleListMissingRequirements}
                      disabled={chat.isLoading}
                      className="w-full p-2 text-left hover:bg-gray-50 text-sm text-[#657071] rounded border border-gray-200 disabled:opacity-50"
                    >
                      ❌ List missing requirements
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Evaluations Tab */}
            {activeTab === 'evaluations' && (
              <div className="flex-1 p-6">
                {isLoadingEvaluations ? (
                  <div className="flex items-center justify-center h-32">
                    <LoadingSpinner size="md" />
                  </div>
                ) : (
                  <>
                    <h3 className="text-sm font-medium text-gray-900 mb-4">
                      {evaluations?.length || 0} Evaluations
                    </h3>
                    <div>
                      <table className="w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Requirement
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Submitted
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Evaluation
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {evaluations?.map((evaluation) => (
                            <tr key={evaluation.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-center">
                                <div className="text-xs font-medium text-gray-900">
                                  {evaluation.requirement?.title || evaluation.requirement?.requirement_id}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-center">
                                {evaluation.submission_status === 'submitted' ? (
                                  evaluation.requirement?.is_mandatory ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      Yes
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      Yes
                                    </span>
                                  )
                                ) : (
                                  evaluation.requirement?.is_mandatory ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      No
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                      No
                                    </span>
                                  )
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="inline-flex items-center justify-center">
                                  {editingEvaluationId === evaluation.id ? (
                                    <Select
                                      value={evaluation.result}
                                      onChange={(e) => handleEvaluationChange(evaluation.id, e.target.value as 'meets' | 'partially_meets' | 'not_met')}
                                      options={[
                                        { value: 'meets', label: 'Yes' },
                                        { value: 'partially_meets', label: 'Partially' },
                                        { value: 'not_met', label: 'No' }
                                      ]}
                                      className="text-xs w-24"
                                    />
                                  ) : (
                                    <Tooltip
                                      content={
                                        <div className="max-w-xs max-h-96 overflow-y-auto">
                                          <div className="font-semibold mb-1">Evidence:</div>
                                          <div className="mb-2 text-sm">
                                            {evaluation.evidence || 'No evidence provided'}
                                          </div>
                                          {evaluation.reasoning && (
                                            <>
                                              <div className="font-semibold mb-1">Reasoning:</div>
                                              <div className="text-sm">
                                                {evaluation.reasoning.length > 500 
                                                  ? `${evaluation.reasoning.substring(0, 500)}...` 
                                                  : evaluation.reasoning}
                                              </div>
                                            </>
                                          )}
                                          {evaluation.confidence_score && (
                                            <div className="mt-2 text-xs text-gray-300">
                                              Confidence: {Math.round(evaluation.confidence_score * 100)}%
                                            </div>
                                          )}
                                        </div>
                                      }
                                      position="top"
                                    >
                                      <div className="hover:opacity-80 transition-opacity">
                                        {evaluation.result === 'meets' ? (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            Yes
                                          </span>
                                        ) : evaluation.result === 'partially_meets' ? (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                            Partially
                                          </span>
                                        ) : evaluation.result === 'not_evaluated' ? (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                            N/A
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            No
                                          </span>
                                        )}
                                      </div>
                                    </Tooltip>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {/* View document button - show when section was submitted */}
                                  {evaluation.submission_status === 'submitted' && (evaluation.source_file_path || evaluation.mapped_files?.length > 0) ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs px-2 py-1"
                                      onClick={() => {
                                        highlightEvidenceInPDF(evaluation)
                                      }}
                                    >
                                      View
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-gray-400 w-12">-</span>
                                  )}
                                  
                                  {/* Edit button */}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs px-1 py-1 h-6 w-6"
                                    onClick={() => toggleEditEvaluation(evaluation.id)}
                                    title="Edit evaluation"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default VendorResponseDetailPage